import { SortDirection } from '@angular/material/sort';
import { compareLocale } from '@nimbus-library/features/localization/utils/active-locale';

export interface NimbusGridUntyped {
  field?: string;
  dir?: SortDirection;
}

export interface NimbusGridSort<TModel = any> {
  field?: keyof TModel & string;
  dir?: SortDirection;
}

export function orderBySubPropertiesIterator(value) {
  const separator = '.';
  const properties = value[0].field.split(separator);

  return (item) => {
    const propertyValue = properties.reduce((prev, curr) => prev && prev[curr], item);
    if (!propertyValue) {
      return propertyValue;
    }
    if (propertyValue.toLowerCase) {
      return propertyValue.toLowerCase();
    }
    if (Array.isArray(propertyValue)) {
      return propertyValue.join('').toLowerCase();
    }
    return propertyValue;
  };
}

// Same ordering as lodash orderBy, except strings collate in the active language: null, undefined and NaN sort last ascending.
function compareGridValues(first: unknown, second: unknown): number {
  if (typeof first === 'string' && typeof second === 'string') {
    return compareLocale(first, second);
  }
  const firstMissing = first === null || first === undefined || Number.isNaN(first);
  const secondMissing = second === null || second === undefined || Number.isNaN(second);
  if (firstMissing || secondMissing) {
    return Number(firstMissing) - Number(secondMissing);
  }
  return Number((first as number) > (second as number)) - Number((first as number) < (second as number));
}

function sortGridData<T>(data: T[], value: NimbusGridUntyped[]): T[] {
  const iterator = orderBySubPropertiesIterator(value);
  const ascending = value[0].dir === 'asc';
  return [...(data ?? [])]
    .map((item) => ({ item, key: iterator(item) }))
    .sort((first, second) => (ascending ? compareGridValues(first.key, second.key) : compareGridValues(second.key, first.key)))
    .map(({ item }) => item);
}

export function orderBy<T>(data: T[], value: NimbusGridSort<T>[]) {
  return value && value.length ? sortGridData(data, value) : data;
}

export function orderByTyped<T1, T2>(data: T1[], value: NimbusGridSort<T2>[]): any[] {
  return value && value.length ? sortGridData(data, value) : data;
}
